import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { toast } from 'sonner';
import { Camera, Phone, MapPin, Copy, RefreshCw, ChevronDown, Clock, LogOut, FileText, Navigation, Wrench, Truck } from 'lucide-react';

const LIVREUR_STATUTS = {
  // "À confirmer" — assignée, en attente
  a_confirmer: { label: 'À confirmer', color: 'bg-orange-500', bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200' },
  // "En cours" — confirmed/started
  en_cours: { label: 'En cours', color: 'bg-blue-500', bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
  // "Terminé" — done
  termine: { label: 'Terminé', color: 'bg-emerald-500', bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200' },
};

function getLivreurStatut(item) {
  const s = item.statut || '';
  if (['terminee', 'livre', 'livree'].includes(s)) return 'termine';
  if (['en_cours'].includes(s)) return 'en_cours';
  // assignee, a_planifier, planifiee → need confirmation
  return 'a_confirmer';
}

function buildAddress(client) {
  // Use structured fields first (adresse + code_postal + ville)
  const adr = client?.adresse || '';
  const cp = client?.code_postal_livraison || client?.code_postal || '';
  const ville = client?.ville_livraison || client?.ville || '';
  if (adr && (cp || ville)) return `${adr}, ${cp} ${ville}`.trim();
  // Fallback: parse adresse_livraison to extract street + city (skip company name / phone)
  const raw = client?.adresse_livraison || '';
  if (raw) {
    const lines = raw.split('\n').map(l => l.trim()).filter(Boolean);
    // Find lines that look like addresses (start with a number or contain known patterns)
    const addrLines = lines.filter(l => /^\d/.test(l) || /\d{5}/.test(l));
    if (addrLines.length) return addrLines.join(', ');
    // Last resort: use last 2 lines (usually street + city)
    return lines.slice(-2).join(', ');
  }
  return '';
}

function getDept(client) {
  const cp = client?.code_postal_livraison || client?.code_postal || '';
  return cp.substring(0, 2) || '—';
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    const jours = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
    return `${jours[d.getDay()]} ${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')}`;
  } catch { return dateStr; }
}

function formatHeure(item) {
  if (item.heure_debut) return `${item.heure_debut}${item.heure_fin ? ` - ${item.heure_fin}` : ''}`;
  if (item.date && item.date.includes('T')) {
    const timePart = item.date.split('T')[1];
    if (timePart) return timePart.substring(0, 5);
  }
  return '';
}

export default function LivreurPage() {
  const { user, logout } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [sendingNote, setSendingNote] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ done: 0, total: 0 });
  const [showClose, setShowClose] = useState(null);
  const [compteRendu, setCompteRendu] = useState('');
  const [filter, setFilter] = useState('all');
  const [photoThumbs, setPhotoThumbs] = useState({});

  const [refuseId, setRefuseId] = useState(null);
  const [refuseReason, setRefuseReason] = useState('');
  const [refusing, setRefusing] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/livreur/mon-planning');
      setItems(res.data.items || []);
    } catch { toast.error('Erreur chargement'); }
    finally { setLoading(false); }
  }, []);

  const fetchPhotos = useCallback(async (item) => {
    if (photoThumbs[item.id]) return;
    try {
      const type = item._type === 'intervention' ? 'intervention' : 'livraison';
      const res = await api.get(`/livreur/photos/${type}/${item.id}`);
      setPhotoThumbs(prev => ({ ...prev, [item.id]: res.data || [] }));
    } catch { }
  }, [photoThumbs]);

  const handleExpand = (item) => {
    const newExpanded = expanded === item.id ? null : item.id;
    setExpanded(newExpanded);
    if (newExpanded && item.photo_count > 0) {
      fetchPhotos(item);
    }
  };

  useEffect(() => { fetchData(); }, [fetchData]);

  // "Refuser" — refuse a mission
  const refuserMission = async (item) => {
    setRefusing(true);
    try {
      if (item._type === 'intervention') {
        await api.put(`/livreur/refuser-intervention/${item.id}`, { raison: refuseReason });
      } else {
        await api.put(`/livreur/refuser/${item.id}`, { raison: refuseReason });
      }
      toast.success('Mission refusée');
      setRefuseId(null);
      setRefuseReason('');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setRefusing(false); }
  };

  // "Je prends en charge" → directly set en_cours
  const prendreEnCharge = async (item) => {
    try {
      if (item._type === 'intervention') {
        await api.put(`/interventions/${item.id}/statut`, { statut: 'en_cours', note: 'Pris en charge par le technicien' });
      } else {
        await api.put(`/livreur/valider/${item.id}`);
      }
      toast.success('Pris en charge');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  // "Terminer" → needs compte-rendu
  const terminer = async (item) => {
    if (!compteRendu.trim()) {
      toast.error('Compte-rendu obligatoire');
      return;
    }
    try {
      if (item._type === 'intervention') {
        await api.put(`/interventions/${item.id}/statut`, { statut: 'terminee', compte_rendu: compteRendu, note: 'Terminée par le technicien' });
      } else {
        await api.put(`/livreur/valider/${item.id}`, { compte_rendu: compteRendu });
      }
      toast.success('Terminé');
      setShowClose(null);
      setCompteRendu('');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const addNote = async (item) => {
    if (!noteText.trim()) return;
    setSendingNote(true);
    try {
      if (item._type === 'intervention') {
        await api.post(`/interventions/${item.id}/notes`, { texte: noteText });
      }
      toast.success('Note ajoutée');
      setNoteText('');
      fetchData();
    } catch { toast.error('Erreur'); }
    finally { setSendingNote(false); }
  };

  const uploadPhotos = async (files, item) => {
    if (!files?.length) return;
    const clientId = item.client?.id;
    if (!clientId) { toast.error('Client introuvable'); return; }
    setUploading(true);
    setUploadProgress({ done: 0, total: files.length });
    let ok = 0;
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('client_id', clientId);
        if (item._type === 'intervention') {
          fd.append('intervention_id', item.id);
          fd.append('type_doc', 'photo_sav');
          await api.post(`/interventions/${item.id}/photos`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        } else {
          fd.append('commande_id', item.id);
          fd.append('type_doc', 'photo_livraison');
          await api.post(`/documents/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        }
        ok++;
      } catch (e) {
        console.error('Upload error:', e);
        toast.error(e.response?.data?.detail || `Erreur upload ${file.name}`);
      }
      setUploadProgress(prev => ({ ...prev, done: prev.done + 1 }));
    }
    if (ok) toast.success(`${ok} photo${ok > 1 ? 's' : ''} uploadée${ok > 1 ? 's' : ''}`);
    setUploading(false);
    setUploadProgress({ done: 0, total: 0 });
    setPhotoThumbs(prev => { const next = { ...prev }; delete next[item.id]; return next; });
    fetchData();
  };

  const copyAddress = (client) => {
    const addr = buildAddress(client);
    navigator.clipboard?.writeText(addr).then(() => toast.success('Adresse copiée')).catch(() => {
      // Fallback for non-HTTPS
      const ta = document.createElement('textarea');
      ta.value = addr;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      toast.success('Adresse copiée');
    });
  };

  const openWaze = (client) => {
    const addr = buildAddress(client);
    window.open(`https://waze.com/ul?q=${encodeURIComponent(addr)}`, '_blank');
  };

  const callClient = (client) => {
    const tel = client?.tel_portable || client?.tel_salon || client?.telephone || client?.tel || '';
    if (tel) {
      const cleaned = tel.replace(/\s+/g, '');
      window.location.href = `tel:${cleaned}`;
    } else {
      toast.error('Pas de numéro de téléphone');
    }
  };

  // Filter items
  const filtered = items.filter(item => {
    const ls = getLivreurStatut(item);
    if (filter === 'a_confirmer') return ls === 'a_confirmer';
    if (filter === 'en_cours') return ls === 'en_cours';
    if (filter === 'termine') return ls === 'termine';
    return true;
  });

  // Sort: À confirmer first, then En cours, then Terminé
  const sorted = [...filtered].sort((a, b) => {
    const order = { a_confirmer: 0, en_cours: 1, termine: 2 };
    const diff = (order[getLivreurStatut(a)] || 0) - (order[getLivreurStatut(b)] || 0);
    if (diff !== 0) return diff;
    return (a.date || '').localeCompare(b.date || '');
  });

  const counts = {
    all: items.length,
    a_confirmer: items.filter(i => getLivreurStatut(i) === 'a_confirmer').length,
    en_cours: items.filter(i => getLivreurStatut(i) === 'en_cours').length,
    termine: items.filter(i => getLivreurStatut(i) === 'termine').length,
  };

  if (loading) return (
    <div className="flex items-center justify-center h-screen bg-gray-50">
      <RefreshCw size={24} className="animate-spin text-gray-400" />
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 pb-6" data-testid="livreur-page">
      {/* Header */}
      <div className="bg-[hsl(222,47%,11%)] text-white px-4 pt-6 pb-4" data-testid="livreur-header">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-lg font-bold">Mon Planning</h1>
            <p className="text-xs text-white/60">{user?.prenom || 'Technicien'}</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={fetchData} className="p-2 rounded-lg bg-white/10 active:bg-white/20" data-testid="refresh-btn">
              <RefreshCw size={18} />
            </button>
            <button onClick={logout} className="p-2 rounded-lg bg-white/10 active:bg-white/20" data-testid="logout-btn">
              <LogOut size={18} />
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-2 overflow-x-auto pb-1" data-testid="filter-tabs">
          {[
            { key: 'all', label: 'Tout', count: counts.all },
            { key: 'a_confirmer', label: 'À confirmer', count: counts.a_confirmer },
            { key: 'en_cours', label: 'En cours', count: counts.en_cours },
            { key: 'termine', label: 'Terminé', count: counts.termine },
          ].map(f => (
            <button key={f.key} onClick={() => setFilter(f.key)}
              className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${filter === f.key ? 'bg-white text-[hsl(222,47%,11%)]' : 'bg-white/10 text-white/80'}`}
              data-testid={`filter-${f.key}`}>
              {f.label} {f.count > 0 && <span className="ml-1 opacity-70">({f.count})</span>}
            </button>
          ))}
        </div>
      </div>

      {/* Items */}
      <div className="px-4 pt-4 space-y-3" data-testid="missions-list">
        {sorted.length === 0 && (
          <div className="text-center py-12 text-gray-400 text-sm" data-testid="empty-state">
            Aucune mission
          </div>
        )}

        {sorted.map(item => {
          const ls = getLivreurStatut(item);
          const cfg = LIVREUR_STATUTS[ls];
          const isExpanded = expanded === item.id;
          const isIntervention = item._type === 'intervention';
          const dept = getDept(item.client);
          const dateStr = formatDate(item.date);
          const heureStr = formatHeure(item);
          const clientName = item.client?.societe || '—';

          return (
            <div key={item.id}
              className={`bg-white rounded-xl border ${cfg.border} shadow-sm overflow-hidden transition-all`}
              data-testid={`mission-card-${item.id}`}>

              {/* Card header — always visible */}
              <button onClick={() => handleExpand(item)} className="w-full text-left" data-testid={`expand-${item.id}`}>
                <div className="flex items-stretch">
                  {/* Left color bar */}
                  <div className={`w-1.5 ${cfg.color} flex-shrink-0`} />

                  {/* Content */}
                  <div className="flex-1 p-3 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        {isIntervention
                          ? <Wrench size={14} className="text-amber-500 flex-shrink-0" />
                          : <Truck size={14} className="text-blue-500 flex-shrink-0" />
                        }
                        <span className="font-semibold text-sm text-gray-900 truncate">{clientName}</span>
                      </div>
                      {/* Department badge */}
                      <span className="flex-shrink-0 text-base font-black text-gray-800 bg-gray-100 rounded-lg px-2.5 py-0.5" data-testid={`dept-${item.id}`}>
                        {dept}
                      </span>
                    </div>

                    {/* Date, heure, statut */}
                    <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                      {dateStr && (
                        <span className="text-xs text-gray-500 flex items-center gap-1">
                          <Clock size={11} /> {dateStr}
                        </span>
                      )}
                      {heureStr && <span className="text-xs font-semibold text-gray-700">{heureStr}</span>}
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${cfg.bg} ${cfg.text}`}>
                        {cfg.label}
                      </span>
                      {item.photo_count > 0 && (
                        <span className="text-[10px] font-medium text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-full flex items-center gap-0.5">
                          <Camera size={10} /> {item.photo_count}
                        </span>
                      )}
                    </div>

                    {/* Subtitle */}
                    <p className="text-xs text-gray-400 mt-1 truncate">
                      {isIntervention
                        ? item.titre
                        : (item.lignes?.length
                          ? item.lignes.map(l => `${l.quantite || 1}x ${l.reference || l.designation || ''}`).join(' · ')
                          : item.modele || '')}
                    </p>
                  </div>

                  {/* Expand chevron */}
                  <div className="flex items-center px-2 text-gray-300">
                    <ChevronDown size={16} className={`transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                  </div>
                </div>
              </button>

              {/* Expanded content */}
              {isExpanded && (
                <div className="border-t border-gray-100 px-4 py-3 space-y-3" data-testid={`expanded-${item.id}`}>
                  {/* Address & shortcuts */}
                  <div className="flex items-start gap-2">
                    <MapPin size={14} className="text-gray-400 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-gray-600 flex-1">{buildAddress(item.client) || '—'}</p>
                  </div>

                  <div className="flex gap-2" data-testid={`shortcuts-${item.id}`}>
                    <button onClick={() => openWaze(item.client)} className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-[#33ccff]/10 text-[#0099cc] rounded-lg text-xs font-medium active:bg-[#33ccff]/20" data-testid={`waze-${item.id}`}>
                      <Navigation size={14} /> Waze
                    </button>
                    <button onClick={() => copyAddress(item.client)} className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-gray-100 text-gray-600 rounded-lg text-xs font-medium active:bg-gray-200" data-testid={`copy-addr-${item.id}`}>
                      <Copy size={14} /> Copier
                    </button>
                    <button onClick={() => callClient(item.client)} className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-emerald-50 text-emerald-600 rounded-lg text-xs font-medium active:bg-emerald-100" data-testid={`call-${item.id}`}>
                      <Phone size={14} /> {(item.client?.tel_portable || item.client?.tel_salon || 'Appeler').replace(/(\d{2})(?=\d)/g, '$1 ').trim().slice(0, 14)}
                    </button>
                  </div>

                  {/* Description for interventions */}
                  {isIntervention && item.description && (
                    <div className="bg-gray-50 rounded-lg p-2.5">
                      <p className="text-xs text-gray-600">{item.description}</p>
                    </div>
                  )}

                  {/* Action buttons based on statut */}
                  {ls === 'a_confirmer' && refuseId !== item.id && (
                    <div className="flex gap-2">
                      <button onClick={() => prendreEnCharge(item)}
                        className="flex-1 py-2.5 bg-orange-500 text-white rounded-lg text-sm font-bold active:bg-orange-600 transition-colors"
                        data-testid={`confirm-btn-${item.id}`}>
                        Je prends en charge
                      </button>
                      <button onClick={() => setRefuseId(item.id)}
                        className="px-4 py-2.5 border-2 border-red-200 text-red-500 rounded-lg text-sm font-bold active:bg-red-50 transition-colors"
                        data-testid={`refuse-btn-${item.id}`}>
                        Refuser
                      </button>
                    </div>
                  )}

                  {ls === 'a_confirmer' && refuseId === item.id && (
                    <div className="space-y-2 bg-red-50 rounded-lg p-3" data-testid={`refuse-form-${item.id}`}>
                      <p className="text-xs font-bold text-red-700">Raison du refus</p>
                      <input value={refuseReason} onChange={e => setRefuseReason(e.target.value)}
                        className="w-full text-sm border border-red-200 rounded-lg px-3 py-2 focus:ring-1 focus:ring-red-300"
                        placeholder="Ex: trop loin, pas disponible..." data-testid={`refuse-reason-${item.id}`} />
                      <div className="flex gap-2">
                        <button onClick={() => { setRefuseId(null); setRefuseReason(''); }}
                          className="flex-1 py-2 border border-gray-200 text-gray-600 rounded-lg text-sm font-medium">
                          Annuler
                        </button>
                        <button onClick={() => refuserMission(item)} disabled={refusing}
                          className="flex-1 py-2 bg-red-500 text-white rounded-lg text-sm font-bold active:bg-red-600 disabled:opacity-50"
                          data-testid={`confirm-refuse-${item.id}`}>
                          {refusing ? <RefreshCw size={14} className="animate-spin mx-auto" /> : 'Confirmer le refus'}
                        </button>
                      </div>
                    </div>
                  )}

                  {ls === 'en_cours' && !showClose && (
                    <button onClick={() => setShowClose(item.id)}
                      className="w-full py-2.5 bg-emerald-500 text-white rounded-lg text-sm font-bold active:bg-emerald-600 transition-colors"
                      data-testid={`finish-btn-${item.id}`}>
                      Terminer
                    </button>
                  )}

                  {ls === 'en_cours' && showClose === item.id && (
                    <div className="space-y-2 bg-emerald-50 rounded-lg p-3" data-testid={`close-form-${item.id}`}>
                      <p className="text-xs font-bold text-emerald-700">Compte-rendu</p>
                      <textarea value={compteRendu} onChange={e => setCompteRendu(e.target.value)}
                        className="w-full text-sm border border-emerald-200 rounded-lg p-2.5 focus:ring-1 focus:ring-emerald-300"
                        rows={3} placeholder="Décrivez le travail effectué..." data-testid={`compte-rendu-${item.id}`} />
                      <div className="flex gap-2">
                        <button onClick={() => { setShowClose(null); setCompteRendu(''); }}
                          className="flex-1 py-2 border border-gray-200 text-gray-600 rounded-lg text-sm font-medium">
                          Annuler
                        </button>
                        <button onClick={() => terminer(item)}
                          className="flex-1 py-2 bg-emerald-500 text-white rounded-lg text-sm font-bold active:bg-emerald-600"
                          data-testid={`confirm-finish-${item.id}`}>
                          Valider
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Notes terrain (interventions only) */}
                  {isIntervention && ls !== 'termine' && (
                    <div className="space-y-2" data-testid={`notes-section-${item.id}`}>
                      <p className="text-xs font-bold text-gray-600 flex items-center gap-1"><FileText size={12} /> Notes terrain</p>
                      {(item.notes_terrain || []).map(n => (
                        <div key={n.id} className="bg-gray-50 rounded-lg p-2">
                          <p className="text-xs text-gray-700">{n.texte}</p>
                          <p className="text-[10px] text-gray-400 mt-0.5">{n.auteur}</p>
                        </div>
                      ))}
                      <div className="flex gap-2">
                        <input value={noteText} onChange={e => setNoteText(e.target.value)}
                          className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-2 focus:ring-1 focus:ring-blue-300"
                          placeholder="Ajouter une note..." data-testid={`note-input-${item.id}`} />
                        <button onClick={() => addNote(item)} disabled={sendingNote || !noteText.trim()}
                          className="px-4 py-2 bg-[hsl(222,47%,11%)] text-white rounded-lg text-sm font-medium disabled:opacity-50"
                          data-testid={`note-submit-${item.id}`}>
                          {sendingNote ? <RefreshCw size={14} className="animate-spin" /> : 'OK'}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Photos section */}
                  {ls !== 'a_confirmer' && (
                    <div className="space-y-2" data-testid={`photos-section-${item.id}`}>
                      <div className="flex items-center justify-between">
                        <p className="text-xs font-bold text-gray-600 flex items-center gap-1"><Camera size={12} /> Photos</p>
                        {item.photo_count > 0 && (
                          <span className="text-xs font-bold px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full" data-testid={`photo-count-${item.id}`}>
                            {item.photo_count} photo{item.photo_count > 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                      {/* Thumbnail preview */}
                      {photoThumbs[item.id] && photoThumbs[item.id].length > 0 && (
                        <div className="flex gap-2 overflow-x-auto pb-1" data-testid={`photo-preview-${item.id}`}>
                          {photoThumbs[item.id].slice(0, 4).map((p, idx) => (
                            <div key={p.id || idx} className="w-16 h-16 rounded-lg overflow-hidden border border-gray-200 flex-shrink-0 bg-gray-50">
                              {p.thumbnail && <img src={p.thumbnail} alt="" className="w-full h-full object-cover" />}
                            </div>
                          ))}
                          {item.photo_count > 4 && (
                            <div className="w-16 h-16 rounded-lg border border-gray-200 flex-shrink-0 bg-gray-100 flex items-center justify-center">
                              <span className="text-xs font-bold text-gray-500">+{item.photo_count - 4}</span>
                            </div>
                          )}
                        </div>
                      )}
                      {ls !== 'termine' && (
                        <label className="block cursor-pointer">
                          <div className="border-2 border-dashed border-gray-200 rounded-xl p-4 text-center active:bg-gray-50 transition-colors">
                            {uploading && uploadProgress.total > 0 ? (
                              <div className="space-y-2">
                                <RefreshCw size={20} className="animate-spin text-blue-500 mx-auto" />
                                <p className="text-sm font-bold text-blue-600">{uploadProgress.done}/{uploadProgress.total}</p>
                                <div className="w-full bg-gray-200 rounded-full h-2">
                                  <div className="bg-blue-500 h-2 rounded-full transition-all" style={{ width: `${(uploadProgress.done / uploadProgress.total) * 100}%` }} />
                                </div>
                              </div>
                            ) : (
                              <>
                                <Camera size={24} className="text-gray-400 mx-auto mb-1" />
                                <p className="text-sm font-medium text-gray-700">
                                  {item.photo_count > 0 ? 'Ajouter des photos' : 'Prendre des photos'}
                                </p>
                              </>
                            )}
                          </div>
                          <input type="file" accept="image/*" multiple className="hidden"
                            onChange={e => { uploadPhotos(e.target.files, item); e.target.value = ''; }}
                            disabled={uploading} data-testid={`photo-input-${item.id}`} />
                        </label>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
